import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';

export default function PickARestaurantPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="Pick a Restaurant for Me — Let Hangrily Choose Where to Eat"
        description="Can't pick a restaurant? Let Hangrily do it. Swipe through nearby restaurants filtered by cuisine, price & mood. Our algorithm picks the perfect match for you, your partner, or your group in under 2 minutes. Free, no signup needed."
        path="/pick-a-restaurant-for-me"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Pick a Restaurant for Me - Hangrily',
            description: 'Let Hangrily pick a restaurant for you using smart preference matching and location-based search.',
            url: 'https://hangrily.app/pick-a-restaurant-for-me'
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'Pick a Restaurant for Me', item: 'https://hangrily.app/pick-a-restaurant-for-me' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'How can I get someone to pick a restaurant for me?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Hangrily is like having a personal restaurant concierge. Tell it your cuisine preferences, price range, and mood. It finds nearby restaurants matching your criteria and lets you swipe through them. In 2 minutes, it picks the perfect place. Works for solo decisions, date nights, or group dinners.'
                }
              },
              {
                '@type': 'Question',
                name: 'What is the best app to pick a restaurant?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Hangrily is the #1 rated restaurant picker app. Unlike random wheel spinners, it uses your actual preferences, real Google Places data with photos and ratings, and supports couple and group mode where everyone gets a vote.'
                }
              },
              {
                '@type': 'Question',
                name: 'Can an app choose a restaurant for a group?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes! Hangrily\'s Group Mode supports up to 10 people. Everyone joins with a code, swipes through the same restaurants, and the app finds the one with the most consensus. It\'s democratic, fair, and takes about 3 minutes.'
                }
              },
              {
                '@type': 'Question',
                name: 'How do I pick a restaurant when I can\'t decide?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Decision fatigue is real. Hangrily solves it by showing you one restaurant at a time instead of an overwhelming list. You make simple yes/no decisions on each card. After 15 restaurants and up to 3 rounds, the app narrows it down to the best match. It works because it removes the paradox of choice.'
                }
              }
            ]
          }
        ]}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>Pick a Restaurant for Me</h1>
          <p className="hero-subtitle">
            Too tired to decide? Let Hangrily pick the perfect restaurant in under 2 minutes.
          </p>
        </section>

        <section className="content-section">
          <div className="two-column-layout">
            <div className="column-text">
              <h2>"Just Pick Something" — Done.</h2>
              <p>
                We've all been there. You're starving, staring at your phone, completely unable to pick a restaurant. 
                You open Google Maps and see 50 options. Yelp has 200 reviews for each one. Your brain shuts down. 
                <strong> You just want someone to pick for you.</strong>
              </p>
              <p>
                That's exactly what Hangrily does. Tell it what you're craving (or let it surprise you), and it 
                shows you restaurant cards one at a time. Swipe right on ones that look good, left on ones that don't. 
                In about 2 minutes, Hangrily narrows 15+ options down to THE perfect pick.
              </p>
              <p>
                And if you're deciding with your partner or friends? Everyone swipes independently, and the app 
                finds a restaurant everyone agrees on. <strong>No arguments. No vetoes. Just dinner.</strong>
              </p>
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
                Pick My Restaurant →
              </button>
            </div>
            <div className="column-visual">
              <div className="stat-highlight-grid">
                <div className="stat-highlight">
                  <span className="stat-number-lg">2 min</span>
                  <span className="stat-label-sm">From "I can't decide" to "let's go"</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">15+</span>
                  <span className="stat-label-sm">Real restaurants near you</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">94%</span>
                  <span className="stat-label-sm">Users happy with their pick</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">3</span>
                  <span className="stat-label-sm">Smart rounds to find your match</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Why You Can't Pick a Restaurant (And How to Fix It)</h2>
          <div className="reason-cards">
            <div className="reason-card">
              <div className="reason-icon">🧠</div>
              <h3>Decision Fatigue</h3>
              <p>By dinner time, you've made 35,000+ decisions already. Your brain literally can't handle another one. Hangrily reduces it to simple yes/no swipes — the easiest decision you'll make all day.</p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">🌀</div>
              <h3>Paradox of Choice</h3>
              <p>More options = more anxiety. Google Maps showing 50 restaurants paralyzes you. Hangrily shows one at a time, making each choice effortless. Like scrolling TikTok, but for food.</p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">💑</div>
              <h3>Group Dynamics</h3>
              <p>One person wants sushi, another wants pizza, someone's on a budget. Negotiating is exhausting. Hangrily lets everyone vote independently, then finds the best compromise automatically.</p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">How Hangrily Picks for You</h2>
          <div className="numbered-steps">
            <div className="numbered-step">
              <div className="step-num">1</div>
              <div className="step-content">
                <h3>Tell Us Your Mood</h3>
                <p>Craving Italian? Want something cheap? Feeling adventurous? Set your filters or leave them open for maximum variety. Hangrily finds real restaurants near your location.</p>
              </div>
            </div>
            <div className="numbered-step">
              <div className="step-num">2</div>
              <div className="step-content">
                <h3>Swipe Through Options</h3>
                <p>See restaurant cards with real photos, ratings, price levels, and distance. Swipe right to like, left to pass, up to love. It takes about 30 seconds per round.</p>
              </div>
            </div>
            <div className="numbered-step">
              <div className="step-num">3</div>
              <div className="step-content">
                <h3>Get Your Perfect Pick</h3>
                <p>After up to 3 rounds, Hangrily reveals your top match plus runner-ups. Get directions, see full details, and go eat. The whole thing takes under 2 minutes.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Works for Every Scenario</h2>
          <div className="use-case-grid">
            <div className="use-case-card">
              <span className="use-case-icon">🍽️</span>
              <h3>Solo Lunch</h3>
              <p>On your lunch break and can't decide? Solo mode picks for you in 60 seconds flat.</p>
              <Link to="/where-should-i-eat" className="use-case-link">Solo Decisions →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">💑</span>
              <h3>Date Night</h3>
              <p>Both swipe and match on restaurants you both want. Eliminates the "I don't care, you pick" cycle.</p>
              <Link to="/date-night-restaurants" className="use-case-link">Date Night →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">👥</span>
              <h3>Friend Group</h3>
              <p>Up to 10 people join and vote. Democratic, fair, and fun. Perfect for group dinners.</p>
              <Link to="/group-restaurant-decision" className="use-case-link">Group Mode →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">🏢</span>
              <h3>Team Lunch</h3>
              <p>Coworkers never agree on food. Hangrily finds common ground in 3 minutes. Your team will thank you.</p>
              <Link to="/try" className="use-case-link">Try for Teams →</Link>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How can I get someone to pick a restaurant for me?</h3>
              <p>Hangrily is like a personal restaurant concierge. Set your preferences, swipe through options, and the app picks the best match in 2 minutes. Works solo, with a partner, or with friends.</p>
            </div>
            <div className="faq-item">
              <h3>What's the best app to pick a restaurant?</h3>
              <p>Hangrily is the #1 restaurant picker. Unlike random wheels, it uses real preferences, location data, photos, and ratings to find restaurants you'll actually want to eat at.</p>
            </div>
            <div className="faq-item">
              <h3>Can an app choose for a group?</h3>
              <p>Yes! Hangrily's Group Mode supports up to 10 people. Everyone joins with a code, swipes through the same restaurants, and the app finds consensus.</p>
            </div>
            <div className="faq-item">
              <h3>Is it free?</h3>
              <p>Try free with no account needed. 3 sessions per week for guests. Premium is $9.99/month for unlimited access.</p>
            </div>
          </div>
        </section>

        <section className="cta-banner">
          <h2>Let Hangrily Pick for You</h2>
          <p>Stop overthinking dinner. Swipe, match, eat. Takes 2 minutes.</p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
            Pick My Restaurant Free →
          </button>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
